import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Applogo, Avatar_02 } from "../../Routes/ImagePath";
import headerlogo from "../../assets/img/zolaralogowhite.png";
import { useLocation } from "react-router-dom/dist";
import { logout } from "../../store/slices/AuthSlice";
import { fetchUserDetails } from "../../store/slices/UserSlice";
import UsePostData from "../../hooks/api/PostHook";
import useFetch from "../../hooks/api/fetchHook";
import { toast } from "react-toastify";

const Header = (props) => {
  // State
  const [profile, setProfile] = useState(false);
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redux
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    isLoggedIn,
    userId,
    userToken,
    role,
    userDetails
  } = useSelector((state) => ({
    isLoggedIn: state?.auth?.isLoggedIn,
    userId: state?.auth?.userInfo?.id,
    userToken: state?.auth?.userToken,
    role: state?.auth?.userRole,
    userDetails: state?.user?.userDetails
  }));

  // API Configuration
  const options = useMemo(() => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${userToken}`,
    },
  }), [userToken]);

  // Fetch attendance data
  const { apiData: attendanceData, isError: attendanceError } = useFetch(
    userId ? `${process.env.REACT_APP_BASE_URL}/api/attendance/${userId}` : null,
    options,
    userId
  );
  

  // Post hooks
  const { postData: postPunchOut } = UsePostData(
    `${process.env.REACT_APP_BASE_URL}/api/attendance/punch-out`
  );

  // Memoized user display data
  const { displayName, displayImage } = useMemo(() => {
    const { firstName, lastName, name, image } = userDetails?.data?.user || {};
    return {
      displayName: firstName && lastName ? `${firstName} ${lastName}` : name || "",
      displayImage: image || Avatar_02
    };
  }, [userDetails]);

  // Handlers
  const handlesidebar = useCallback(() => {
    document.body.classList.toggle("mini-sidebar");
    props.setSidebarExpanded(!props.isSidebarExpanded);
  }, [props]);

  const onMenuClik = useCallback(() => {
    document.body.classList.toggle("slide-nav");
  }, []);

  const handleProfile = useCallback(() => {
    setProfile((prev) => !prev);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      if (userId) {
        await postPunchOut({ userId });
      }
      dispatch(logout());
      navigate("/");
    } catch (error) {
      toast.error("Error during logout. Please try again.");
      console.error("Logout error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate, postPunchOut, userId]);

  // Effects
  useEffect(() => {
    if (isLoggedIn && userId) {
      dispatch(fetchUserDetails(userId));
    }
  }, [dispatch, isLoggedIn, userId]);

  useEffect(() => {
    if (attendanceError) {
      toast.error("Error fetching attendance status");
      console.error("Attendance fetch error:", attendanceError);
    }
  }, [attendanceError]);

  useEffect(() => {
    if (attendanceData?.data?.attendances?.length > 0) {
      const lastAttendance = attendanceData.data.attendances[attendanceData.data.attendances.length - 1];
      const lastPunch = lastAttendance?.punches[lastAttendance.punches.length - 1];
      setIsPunchedIn(lastPunch?.type === "in");
    }
  }, [attendanceData]);

  let pathname = location.pathname;

  return (
    <div className="header" style={{ right: "0px" }}>
      {/* Logo */}
      <div
        className="header-left"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Link
          to={role === "employee" ? "/attendance-employee" : "/employees"}
          className="logo"
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              style={{ maxWidth: "170px", height: "auto" }}
              src={headerlogo}
              alt="Company Logo"
            />
          </div>
        </Link>
        <Link
          to={role === "employee" ? "/attendance-employee" : "/employees"}
          className="logo2"
        >
          <img src={Applogo} width={40} height={40} alt="App Logo" />
        </Link>
      </div>
      
      <Link
        id="toggle_btn"
        to="#"
        style={{
          display: pathname.includes("tasks") || pathname.includes("compose") ? "none" : "",
        }}
        onClick={handlesidebar}
      >
        <span className="bar-icon">
          <span />
          <span />
          <span />
        </span>
      </Link>

      <Link
        id="mobile_btn"
        className="mobile_btn"
        to="#"
        onClick={onMenuClik}
      >
        <i className="fa fa-bars" />
      </Link>
      
      {/* Header Menu */}
      <ul className="nav user-menu">
        {/* Punch Status */}
        <li className="nav-item" style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div 
              style={{ 
                width: '10px', 
                height: '10px', 
                borderRadius: '50%', 
                backgroundColor: isPunchedIn ? '#55ce63' : '#f62d51',
              }} 
              title={isPunchedIn ? 'Currently Punched In' : 'Currently Punched Out'}
            />
            <span style={{ color: '#fff', fontSize: '14px' }}>
              {isPunchedIn ? 'Punched In' : 'Punched Out'}
            </span>
          </div>
        </li>

        <li className="nav-item dropdown has-arrow main-drop">
          <Link
            to="#"
            className="dropdown-toggle nav-link"
            data-bs-toggle="dropdown"
            onClick={handleProfile}
          >
            <span className="user-img me-1">
              <img src={displayImage} alt="User Avatar" />
              <span className="status online" />
            </span>
            <span>{displayName}</span>
          </Link>
          <div
            className={`dropdown-menu dropdown-menu-end ${
              profile ? "show" : ""
            }`}
          >
            <Link className="dropdown-item" to="/profile">
              My Profile
            </Link>
            <button 
              className="dropdown-item" 
              onClick={handleLogout}
              disabled={isLoading}
            >
              {isLoading ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </li>
      </ul>
      {/* /Header Menu */}
      
      {/* Mobile Menu */}
      <div className="dropdown mobile-user-menu">
        <Link
          to="#"
          className="nav-link dropdown-toggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <i className="fa fa-ellipsis-v" />
        </Link>
        <div className="dropdown-menu dropdown-menu-end dropdown-menu-right">
          <Link className="dropdown-item" to="/profile">
            My Profile
          </Link>
          <button 
            className="dropdown-item" 
            onClick={handleLogout}
            disabled={isLoading}
          >
            {isLoading ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </div>
      {/* /Mobile Menu */}
    </div>
  );
};

export default Header;
